import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgreementEditManager, useAgreementForm } from '@hero/web-hooks';
import type { AgreementFormValues } from '@hero/web-hooks';
import type { Agreement, ImageWithUpload } from '@hero/schema';
import { Form } from '../../../ui/Form';
import { Label } from '../../../ui/Label';
import { Checkbox } from '../../../ui/Checkbox/Checkbox';
import { CustomFormField } from '../../../app/CustomFormField';
import { DateField } from '../../../app/Date';
import { PaymentTermsFields } from '../../../app/PaymentTerms';
import { FileUploader } from '../../../app/File/FileUploader/FileUploader';
import {
  FinancingFieldBadge,
  type FinancingFieldBadgeVariant,
} from '../../../app/FinancingFieldBadge';
import { ClientSelector } from '../../Client/Selector/ClientSelector';

/**
 * Display context for `AgreementForm`.
 *
 * - `'crm'`         — every field is optional. The form is being
 *                     used to keep CRM tidy; nothing should look
 *                     scary. The contract upload is decorated
 *                     `recommended` so the carrier knows that
 *                     attaching a signed PDF now will pay off if
 *                     they later route this client through
 *                     financing.
 * - `'financing'`   — the agreement is part of an apply flow. The
 *                     contract upload is decorated `required`. The
 *                     form itself stays permissive (hard gating
 *                     happens at the consumer's completeness
 *                     layer, see `Financing/components/Requirements`),
 *                     so the chip is the user-visible signal.
 */
export type AgreementFormDisplayFor = 'crm' | 'financing';

const ACCEPTED_PDF_TYPES: Record<string, string[]> = {
  'application/pdf': [],
};

export interface AgreementFormProps {
  /**
   * Form hook produced by {@link useAgreementForm} (or implicitly
   * by {@link useAgreementEditManager}). Required — the form is a
   * dumb renderer of its hook's state, never owns its own state.
   */
  formHook: ReturnType<typeof useAgreementForm> | ReturnType<typeof useAgreementEditManager>['formHook'];
  /**
   * When the form is mounted by `useAgreementEditManager` the
   * loaded agreement is passed in for read-only references (e.g.
   * showing the existing PDF URL inside the file uploader). The
   * form values themselves come from `formHook` — this prop is
   * for ancillary display only.
   */
  agreement?: Agreement;
  /** Lock the counterparty selector (e.g. when opening from a Client workspace). */
  lockedFields?: Array<'counterparty'>;
  /**
   * Visual context for the form's field decoration — only
   * controls which {@link FinancingFieldBadge} chips render, not
   * the form's underlying validation. Defaults to `'crm'`.
   */
  displayFor?: AgreementFormDisplayFor;
}

export function AgreementForm({
  formHook,
  agreement,
  lockedFields = [],
  displayFor = 'crm',
}: AgreementFormProps) {
  const { t } = useTranslation(['m-agreement', 'c-main']);
  const {
    form,
    values,
    handleStartChange,
    handleEndChange,
    handleCounterpartySelect,
    handleTermsChange,
    handleContractPDFChange,
    handleQuotationPDFChange,
    contractPDFFiles,
    quotationPDFFiles,
  } = formHook;

  const isLocked = (field: 'counterparty') => lockedFields.includes(field);
  // Memoize so the prop identity is stable across re-renders. The
  // form watches `values` from RHF, which produces a fresh object on
  // every keystroke; without memoization the `existingUrls` array
  // would be a new reference each render and force `FileUploader` to
  // re-mount its child `FileThumbnail`, which (for PDFs) triggers an
  // expensive browser PDF re-fetch / re-decode.
  const existingContractUrls = useMemo(() => {
    const url = pickImageUrl(agreement?.contractPDF);
    return url ? [url] : undefined;
  }, [agreement?.contractPDF]);
  const existingQuotationUrls = useMemo(() => {
    const url = pickImageUrl(agreement?.quotationPDF);
    return url ? [url] : undefined;
  }, [agreement?.quotationPDF]);

  // The signed contract is the single field whose required-ness
  // really differs between the two contexts. In CRM it's nice to
  // have (great for audit, billing reconciliation, dispute
  // handling); in financing it's a hard pre-req because the
  // lender underwrites against it. We surface that as a small
  // inline chip rather than mutating validation — the form stays
  // permissive and the consumer's completeness gate decides
  // whether the save can proceed downstream.
  const contractChip: FinancingFieldBadgeVariant | null =
    displayFor === 'financing' ? 'required' : 'recommended';
  // Filled once the user picks a fresh file OR the agreement
  // already has a persisted contract. The chip flips to its green
  // "Provided for financing" state — same source of truth as
  // every other `FinancingFieldBadge`.
  const contractSatisfied =
    contractPDFFiles.length > 0 || (existingContractUrls?.length ?? 0) > 0;

  return (
    <Form {...form}>
      <div className="space-y-4">
        <CustomFormField
          control={form.control}
          name="title"
          filedType="input"
          label={t('form.title.title')}
          placeholder={t('form.title.placeholder', {
            year: new Date().getFullYear(),
            defaultValue: 'e.g. Acme Contract {{year}}',
          })}
        />

        <div className="space-y-1.5">
          <Label>{t('form.counterparty.title')}</Label>
          <ClientSelector
            selectedClients={values.counterparty?._id ? String(values.counterparty._id) : undefined}
            onClientsSelected={handleCounterpartySelect}
            placeholder={t('form.counterparty.placeholder')}
            disabled={isLocked('counterparty')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DateField
            id="agreement-starts"
            label={t('form.starts_at.title')}
            placeholder={t('form.starts_at.placeholder', { defaultValue: 'Pick a start date' })}
            value={values.dates?.start}
            onChange={handleStartChange}
          />
          <DateField
            id="agreement-ends"
            label={t('form.ends_at.title')}
            placeholder={t('form.ends_at.placeholder', { defaultValue: 'Pick an end date' })}
            value={values.dates?.end}
            onChange={handleEndChange}
            minDate={values.dates?.start}
          />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="agreement-auto-renew"
            checked={!!values.dates?.autoRenew}
            onCheckedChange={checked =>
              form.setValue('dates.autoRenew', checked === true, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
          <Label htmlFor="agreement-auto-renew" className="cursor-pointer">
            {t('form.auto_renew.title', { defaultValue: 'Auto-renew when this agreement ends' })}
          </Label>
        </div>

        <PaymentTermsFields
          value={values.terms?.payment}
          onChange={next =>
            handleTermsChange({
              payment: next,
              itemsInsuranceCoveragePerVehicle: values.terms?.itemsInsuranceCoveragePerVehicle ?? 0,
            } as AgreementFormValues['terms'])
          }
        />

        <CustomFormField
          control={form.control}
          name="monthlyVehicleVolume"
          filedType="input"
          type="number"
          label={t('form.monthly_volume.title')}
          placeholder={t('form.monthly_volume.placeholder')}
        />

        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label>{t('form.contract_pdf.title', { defaultValue: 'Signed Contract' })}</Label>
              {contractChip ? (
                <FinancingFieldBadge
                  variant={contractChip}
                  satisfied={contractSatisfied}
                />
              ) : null}
            </div>
            <FileUploader
              files={contractPDFFiles}
              onChange={handleContractPDFChange}
              acceptedFileTypes={ACCEPTED_PDF_TYPES}
              label={t('form.contract_pdf.placeholder', { defaultValue: 'Upload signed contract' })}
              existingUrls={existingContractUrls}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('form.quotation_pdf.title', { defaultValue: 'Signed Quotation' })}</Label>
            <FileUploader
              files={quotationPDFFiles}
              onChange={handleQuotationPDFChange}
              acceptedFileTypes={ACCEPTED_PDF_TYPES}
              label={t('form.quotation_pdf.placeholder', {
                defaultValue: 'Upload signed quotation',
              })}
              existingUrls={existingQuotationUrls}
            />
          </div>
        </div>
      </div>
    </Form>
  );
}

function pickImageUrl(image: ImageWithUpload | undefined): string | undefined {
  if (!image) return undefined;
  // `imageWithUploadSchema` allows three shapes:
  //   1. Persisted asset → `{ id, url }` (canonical post-upload)
  //   2. Not-yet-uploaded → `{ upload: 'data:application/pdf;base64,…' | 'https://…' }`
  //   3. Mixed during edit → `{ id, url, upload: <new payload> }`
  //
  // For preview we just want anything browser-renderable. Prefer
  // `url` (cheap, no base64 parsing) and fall back to `upload`, which
  // already passed the urlSchema/base64Schema branch in the zod
  // transform — both forms work with `openFilePreview`.
  if (typeof image.url === 'string' && image.url) return image.url;
  if (typeof image.upload === 'string' && image.upload) return image.upload;
  return undefined;
}
